import { createContext, useContext, type ReactNode } from "react";
import type { TableMeta } from "@tanstack/react-table";
import type {
  ProductGroupId,
  StockLocationId,
  UnitId,
} from "@KassaroAB/shared/types/ids";
import type { RestSupplier } from "@KassaroAB/shared/types/supplier";
import type { RestProduct } from "@KassaroAB/shared/types/product";
import type { RestStockLocation } from "@KassaroAB/shared/types/stockLocation";
import { Currency } from "@KassaroAB/shared/types/enums/currency";
import type {
  ProductStoreStatus,
  ProductSyncProgress,
} from "../../../hooks/useProductStore";
import type { RestBookkeepingAccount } from "@KassaroAB/shared/types/bookkeepingAccount";
import {
  buildProductColumnLabels,
  createProductColumns,
  type ChainProductSaveTarget,
  type HasProductFeature,
} from "./productTableColumns";
import type { useProductTableEdits } from "./useProductTableEdits";
import type { useProductTableFilters } from "./useProductTableFilters";
import type { useProductTableQuery } from "./useProductTableQuery";
import type { useProductTableStock } from "./useProductTableStock";

export interface ProductsTableContextValue {
  canEdit: boolean;
  status: ProductStoreStatus;
  syncProgress: ProductSyncProgress | null;
  lastSyncedAt: Date | null;
  resync: () => Promise<void>;
  forceResync: () => Promise<void>;
  toolbarActions?: ReactNode;
  onDeleteSelected: () => void;
  columnLabels: ReturnType<typeof buildProductColumnLabels>;
  columns: ReturnType<typeof createProductColumns>;
  groups: { id: ProductGroupId; name: string }[];
  hasFeature: HasProductFeature;
  currency: Currency;
  bookkeepingAccounts: RestBookkeepingAccount[];
  tableMeta: TableMeta<RestProduct>;
  filters: ReturnType<typeof useProductTableFilters>;
  query: ReturnType<typeof useProductTableQuery>;
  edits: ReturnType<typeof useProductTableEdits>;
  stock: ReturnType<typeof useProductTableStock>;
  stockLocations: RestStockLocation[];
  stockLocationId: StockLocationId | null;
  defaultStockLocationId: StockLocationId | null;
  setStockLocationId: (id: StockLocationId | null) => void;
  showStockWarningFilter: boolean;
  showChainSaveTarget: boolean;
  chainSaveTarget: ChainProductSaveTarget;
  setChainSaveTarget: (target: ChainProductSaveTarget) => void;
  isOverlayReady: boolean;
  isOverlayError: boolean;
  suppliers: RestSupplier[];
  units: { id: UnitId; name: string }[];
}

export const ProductsTableContext = createContext<ProductsTableContextValue | null>(
  null,
);

export const useProductsTable = (): ProductsTableContextValue => {
  const context = useContext(ProductsTableContext);
  if (!context) {
    throw new Error("useProductsTable must be used within ProductsTableProvider");
  }
  return context;
};
